<template>
  <div
    ref="nav"
    class="the-navigation"
    @[mouseenter]="$emit('mouseover', false)"
    @[mouseleave]="$emit('mouseover', true)"
  >
    <nav
      :class="{
        'is-mobile': isMobile,
        minimized: minimized && !isMobile,
      }"
    >
      <ul>
        <template v-if="orderedRoutes.size && menu.length">
          <NavigationSection
            v-for="([title, inRoutes], i) in orderedRoutes"
            :key="`section-${i}`"
            :title="title"
            :routes="inRoutes"
            :order="order[title]"
          />
        </template>

        <template v-else>
          <NavigationItem
            v-for="(route, index) in menu"
            :key="index"
            :route="route"
          />
        </template>
      </ul>
    </nav>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import { useInvisibleScroll } from '@/composables/invisibleScroll';

import roleManagerMixin from '@/mixins/roleManager.mixin';

import NavigationSection from '@/components/Navigation/NavigationSection.vue';
import NavigationItem from '@/components/Navigation/NavigationItem.vue';
import { ROUTE_CHILDREN_MAP } from '@/components/Navigation/navigationChildRoutes';

export default {
  name: 'TheNavigation',
  components: {
    NavigationSection,
    NavigationItem,
  },
  mixins: [roleManagerMixin],
  data: () => ({
    invisibleScroll: null,
    order: {
      Independent: {
        header: 0,
        items: {
          Início: 0,
        },
      },
      Gestão: {
        header: 1,
        items: {
          Dashboard: 0,
          Promotoras: 1,
          Usuários: 2,
          'Pré-cadastro Corban': 3,
        },
      },
      REC: {
        header: 2,
        items: {
          'Pessoa Física': 0,
          'Pessoa Jurídica': 1,
        },
      },
      FGTS: {
        header: 2,
        items: {
          'Consultar Saldo FGTS': 0,
          'Propostas FGTS': 1,
        },
      },
      CLT: {
        header: 3,
        items: {
          'Consulta Margem': 0,
          Propostas: 1,
          Leilão: 2,
          'Empresas Cadastradas': 3,
          Contratos: 4,
        },
      },
      'Cartão de Crédito': {
        header: 4,
        items: {
          'Simular Crédito': 0,
          Propostas: 1,
        },
      },
      CGV: {
        header: 5,
        items: {
          'Simular Crédito': 0,
          Propostas: 1,
        },
      },
      Inativados: {
        header: 999,
        items: {
          // ...
        },
      },
    },
  }),
  computed: {
    ...mapState({
      minimized: (state) => state.retract.minimized,
      pinned: (state) => state.retract.pinned,
    }),
    ...mapGetters({
      isMobile: 'app/isMobile',
      isInternalUser: 'user/isInternalUser',
    }),
    menu() {
      const arr = [
        {
          icon: 'fas fa-ban',
          href: '/nao-perturbe',
          text: 'Não perturbe',
          'menu-item': 'Gestão',
        },
      ];

      if (this.isTypist) {
        arr.push({
          icon: 'fas fa-home',
          href: '/hub',
          text: 'Início',
          'menu-item': 'Independent',
        });
      }

      // liberação de acesso (whitelist) — restrito ao perfil super
      if (this.isSuper) {
        arr.push({
          icon: 'fas fa-user-lock',
          href: '/liberacao-acesso',
          text: 'Liberação de Acesso',
          'menu-item': 'Gestão',
        });
      }

      try {
        const menuData = JSON.parse(localStorage.getItem('menu'));
        const routers = menuData?.routers || [];

        if (Array.isArray(routers)) {
          for (const route of this.dedupeRoutes(routers)) {
            arr.push(route);
          }
        }
      } catch {
        // ...
      }

      // Enrich every route with its declared child prefixes.
      // NavigationLink uses `route.children` to compute isParentActive.
      return arr.map(this.enrichRoute);
    },
    orderedRoutes() {
      return this.menu.reduce((map, route) => {
        const key = route['menu-item'];

        if (!key) return map;

        if (map.has(key)) map.get(key).push(route);
        else map.set(key, [route]);

        return map;
      }, new Map());
    },
    mouseenter() {
      return this.pinned && !this.isMobile ? 'mouseenter' : null;
    },
    mouseleave() {
      return this.pinned && !this.isMobile ? 'mouseleave' : null;
    },
    shouldShowResponsible() {
      return [
        this.isFinancial,
        this.isSuper,
        this.isManager,
        this.isCommercial,
        this.isCoordenador,
        this.isAdmin,
      ].some((isValid) => isValid);
    },
  },
  watch: {
    $route() {
      this.invisibleScroll.reset();
    },
    minimized() {
      this.invisibleScroll.reset();
    },
    pinned() {
      this.invisibleScroll.reset();
    },
  },
  mounted() {
    this.invisibleScroll = useInvisibleScroll(this.$refs.nav);
  },
  methods: {
    /**
     * Attaches declared child route prefixes to a route object.
     * Enables NavigationLink to highlight the parent when on a child route.
     */
    enrichRoute(route) {
      return {
        ...route,
        children: ROUTE_CHILDREN_MAP[route.href] ?? [],
      };
    },
    normalizeRouteKey(route = {}) {
      const {
        href = '',
        text = '',
        icon = '',
        tag = '',
      } = route;
      const menuItem = route['menu-item'] || '';

      return [menuItem, text, href, icon, tag].join('::');
    },
    dedupeRoutes(arr = []) {
      const seen = new Set();

      return arr.filter((route) => {
        const key = this.normalizeRouteKey(route);

        if (seen.has(key)) return false;

        seen.add(key);
        return true;
      });
    },
  },
};
</script>

<style lang="scss">
.the-navigation {
  @import '@/assets/scss/src/_textVariables.scss';

  nav {
    padding: 1rem 0.5rem;
    font-size: 12px;

    &.minimized {
      a {
        justify-content: center;
      }
    }

    > ul > li {
      margin-bottom: 0;
    }

    ul {
      display: grid;

      > li {
        order: var(--order, initial);
      }
    }

    li {
      margin-bottom: 0.25rem;

      > ul {
        margin-bottom: 1rem;
      }
    }

    a {
      position: relative;

      display: flex;
      padding: 0.5rem 1rem;
      border-radius: 4px;
      justify-content: flex-start;

      font: {
        family: $font-family;
        weight: 500;
        size: 0.75rem;
      }
      color: $color-secondary;

      transition: all 0.6 ease-in-out;
      align-items: center;

      &:hover,
      &.router-link-exact-active,
      &.router-link-active,
      &.is-active {
        background: {
          color: $color-secondary;
          blend-mode: multiply;
        }
        color: $color-light;
      }
    }

    i {
      font-size: 1.25rem;
    }

    span {
      margin-left: 16px !important;

      font: {
        size: 12px;
        weight: inherit;
      }
      line-height: 150%;
    }

    .menu-label {
      color: $color-secondary;
    }

    .chip {
      border-radius: 10px;
      padding: 2px 10px;

      font: {
        family: Inter;
        size: 10px;
        weight: 500;
      }
      text-align: center;

      background-color: #DBEAFE;
      color: #1E40AF;
    }

    &.is-mobile {
      a {
        padding: 0.5rem;
      }

      i {
        font-size: 1rem;
      }
    }

    &.minimized {
      span:not(.menu-label):not(.chip) {
        display: none;
      }

      .menu-label {
        margin: 0 !important;

        display: inline-block;
        width: 100%;

        text-align: center;
        font-size: .625rem;
      }

      .chip {
        position: absolute;
        bottom: -10px;
        left: 0;
        margin-left: 0 !important;

        font-size: 7.5px;
      }
    }
  }
}
</style>
